import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Sparkles, Clock4, LogOut } from "lucide-react";
import { Toaster, toast } from "sonner";
import Section from "../components/Section";
import AppointmentDialog from "../components/AppointmentDialog";
import { useAuth } from "../context/AuthContext";
import {
  listAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointment,
} from "../lib/api";
import { bucketAppointments, formatMonthYear } from "../lib/dateUtils";
import { format } from "date-fns";

const STORAGE_KEY = "maison.appointments.cache";

const useAppointments = () => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await listAppointments();
      setItems(data);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch {}
    } catch (err) {
      console.warn("Failed to load appointments", err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, loading, refresh, online };
};

export default function HomePage() {
  const { items, loading, refresh, online } = useAppointments();
  const { user, logout } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [now, setNow] = useState(new Date());

  // Refresh "now" every minute so reminder badges update
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const buckets = useMemo(() => bucketAppointments(items, now), [items, now]);

  const handleSave = async (payload, id) => {
    try {
      if (id) {
        await updateAppointment(id, payload);
        toast.success("Reservation updated");
      } else {
        await createAppointment(payload);
        toast.success("Reservation reserved");
      }
      await refresh();
    } catch (e) {
      toast.error("Could not save reservation");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAppointment(id);
      toast.success("Reservation removed");
      await refresh();
    } catch (e) {
      toast.error("Could not delete reservation");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (appt) => {
    setEditing(appt);
    setDialogOpen(true);
  };

  return (
    <div className="app-shell">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "rgba(18,18,23,0.95)",
            color: "#F5F5F5",
            border: "1px solid rgba(212,175,55,0.25)",
            backdropFilter: "blur(12px)",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />

      {/* Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl"
        style={{
          background: "rgba(5, 5, 8, 0.7)",
          borderBottom: "1px solid rgba(212, 175, 55, 0.08)",
        }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
              style={{
                background: "linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.02))",
                border: "1px solid rgba(212,175,55,0.3)",
              }}
            >
              <span className="font-serif text-[#D4AF37] text-lg">M</span>
            </div>
            <div className="min-w-0">
              <p className="eyebrow truncate">MF · {user?.name || user?.email?.split("@")[0] || "you"}</p>
              <p className="font-serif text-sm sm:text-base text-[#F5F5F5]/90 -mt-0.5 truncate">
                {format(now, "EEE, d MMM")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Link
              to="/history"
              className="rounded-full p-2 text-[#D4AF37]/80 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
              title="History"
              data-testid="history-link"
            >
              <Clock4 size={16} />
            </Link>
            <button
              onClick={() => logout()}
              className="rounded-full p-2 text-[#8E8E93] hover:text-[#FF453A] hover:bg-[#FF453A]/10 transition-colors"
              title="Sign out"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
            </button>
            <button
              onClick={openCreate}
              className="btn-gold rounded-full px-3 sm:px-4 py-2 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase ml-1"
              data-testid="new-appointment-btn"
            >
              <Plus size={14} strokeWidth={2.4} />
              <span className="hidden sm:inline">Reserve</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 sm:pt-12 pb-6">
        <p className="eyebrow flex items-center gap-2">
          <Sparkles size={11} className="text-[#D4AF37]" /> Curated for you
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F5] mt-3 leading-[1.05]">
          MF <span className="italic text-[#D4AF37]">Reminder</span>
        </h1>
        <p className="text-[#8E8E93] mt-4 max-w-lg leading-relaxed">
          Dont forgot everything, and go for all in.
        </p>

        {!online && (
          <div
            className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase"
            style={{
              background: "rgba(212,175,55,0.06)",
              color: "#D4AF37",
              border: "1px solid rgba(212,175,55,0.25)",
            }}
            data-testid="offline-badge"
          >
            <span className="divider-dot" /> Offline · Viewing cached
          </div>
        )}
      </section>

      {/* Sections */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pb-32 space-y-12 sm:space-y-14" data-testid="home-main">
        <Section
          eyebrow="This Week"
          title="The current week"
          count={buckets.thisWeek.length}
          items={buckets.thisWeek}
          highlight
          onItemClick={openEdit}
          emptyLabel="Nothing on the docket this week."
        />

        {buckets.months.map((m, idx) => (
          <Section
            key={m.key}
            eyebrow={idx === 0 ? "This Month" : idx === 1 ? "Next Month" : formatMonthYear(m.monthDate).split(" ")[0]}
            title={formatMonthYear(m.monthDate)}
            count={m.items.length}
            items={m.items}
            onItemClick={openEdit}
            emptyLabel={`No reservations in ${formatMonthYear(m.monthDate)}.`}
          />
        ))}

        {loading && items.length === 0 && (
          <p className="text-center text-[#8E8E93] text-sm">Composing your calendar…</p>
        )}
      </main>

      {/* Floating CTA for mobile */}
      <button
        onClick={openCreate}
        className="btn-gold rounded-full fixed bottom-6 left-1/2 -translate-x-1/2 sm:hidden inline-flex items-center gap-2 px-6 py-3 text-xs tracking-[0.2em] uppercase shadow-2xl"
        style={{ zIndex: 30 }}
        data-testid="fab-new-appointment"
      >
        <Plus size={15} strokeWidth={2.4} />
        New Reservation
      </button>

      <AppointmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSave={handleSave}
        onDelete={handleDelete}
      />

      <footer className="max-w-3xl mx-auto px-6 pb-10 text-center">
        <div className="gold-hairline w-24 mx-auto mb-4" />
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#8E8E93]">
          MF · Private Edition
        </p>
      </footer>
    </div>
  );
}
